import React, { createContext, useContext, useState, useEffect } from "react";
import { setAuthToken } from "../services/apis/axiosInstance";
import { fetchSecret } from "../config/secret-retriever.config";

const FIREBASE_TOKEN_URL =
  "https://securetoken.googleapis.com/v1/token?key=" +
  fetchSecret("FIREBASE_API_KEY");

const AuthContext = createContext({ token: null, loading: true });

export const useAuth = () => useContext(AuthContext);

function isTokenValid(token) {
  if (!token?.accessToken || !token.expirationTime) return false;
  const exp =
    typeof token.expirationTime === "string"
      ? new Date(token.expirationTime).getTime()
      : token.expirationTime;
  return exp > Date.now();
}

async function refreshFirebaseToken(refreshToken) {
  const res = await fetch(FIREBASE_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `grant_type=refresh_token&refresh_token=${refreshToken}`,
  });
  const data = await res.json();
  if (data.access_token) {
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expirationTime: Date.now() + data.expires_in * 1000,
    };
  }
  return null;
}

// Helper functions for cookies
function setCookie(name, value, days = 7) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; expires=${expires}; path=/`;
}
function getCookie(name) {
  return document.cookie.split("; ").reduce((r, v) => {
    const parts = v.split("=");
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, "");
}
function removeCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const validate = async (showLoading = false) => {
    if (showLoading) setLoading(true);

    const stored = getCookie("token");
    let t = stored ? JSON.parse(stored) : null;

    if (isTokenValid(t)) {
      setAuthToken(t.accessToken);
      setToken(t);
      if (showLoading) setLoading(false);
      return;
    }

    if (t?.refreshToken) {
      const nt = await refreshFirebaseToken(t.refreshToken);
      if (isTokenValid(nt)) {
        setCookie("token", JSON.stringify(nt));
        setAuthToken(nt.accessToken);
        setToken(nt);
        if (showLoading) setLoading(false);
        return;
      }
    }

    removeCookie("token");
    setAuthToken("");
    setToken(null);
    if (showLoading) setLoading(false);
  };

  // Initial mount
  useEffect(() => {
    validate(true);
  }, []);

  // Tab visibility changes (silent revalidate)
  const REFRESH_THRESHOLD = 5 * 60 * 1000;

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      const stored = getCookie("token");
      if (!stored) return; // no token at all

      const t = JSON.parse(stored);
      if (t?.expirationTime) {
        const exp =
          typeof t.expirationTime === "string"
            ? new Date(t.expirationTime).getTime()
            : t.expirationTime;
        const timeLeft = exp - Date.now();

        // if token is still good and not within our threshold, skip calling API
        if (timeLeft > REFRESH_THRESHOLD) {
          return;
        }
      }

      // either expired, about-to-expire, or no expiration info → revalidate
      validate(false);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [REFRESH_THRESHOLD]);

  return (
    <AuthContext.Provider value={{ token, loading, setToken }}>
      {loading ? <div>Loading…</div> : children}
    </AuthContext.Provider>
  );
};
